import React, { useEffect, useState } from "react";
import axios from "axios";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  CircularProgress,
  Snackbar,
  Button,
  Alert,
  Typography,
  Container,
  Paper,
  Input,
  useTheme,
  ThemeProvider,
  createTheme,
  styled,
  Tabs,
  Tab,
  Grid,
} from "@mui/material";
import { setUserId } from "../../Redux/actions";
import App from "views/Chat/Chat";
import NoSavedPosts from "./NoSavedPosts";
import NewAppBar from "components/Navbars/AppBar";
import Footer from "views/Landing/Footer";
import LoginForDialog from "views/Auth/LoginForDialog";
import SignInForDialog from "views/Auth/SignInForDialog";
import MyCardDetails from "../client/CardDetails";
import colors from "colors/color";
import SearchBar from "views/Offers/SearchBar";
import ListPosts from "views/Offers/ListPosts";
import Details from "views/Offers/Details";
import SpontaneousCard from "views/Spontaneous offers/SpontaneousCard";
import smart_logo_white from "../../assets/img/smart_logo_white.svg";
import smart_logo from "../../assets/img/small_logo.svg";

// Styled components
const FullScreenContainer = styled(Box)({
  width: "100%",
  height: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const LoadingContainer = styled(Box)({
  width: "100%",
  height: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: "20px",
});

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderBottom: `2px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'left',
  alignItems: 'center',
  marginBottom: '20px',
  '& .MuiTabs-flexContainer': {
    justifyContent: 'flex-start',
  },
  '& .MuiTab-root': {
    display: 'flex',
    justifyContent: 'left',
    fontWeight: 600,
    fontSize: '1rem',
    textTransform: 'none',
    minWidth: 100,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
      minWidth: 80,
    },
  },
  '& .Mui-selected': {
    color: colors.primary,
  },
  '& .MuiTabs-indicator': {
    backgroundColor: colors.primary,
    height: 3,
  },
}));

function SavedPosts() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // State variables
  const [allJobs, setAllJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isAuth, setIsAuth] = useState(false);
  const [openLoginDialog, setOpenLoginDialog] = useState(false);
  const [openSignInDialog, setOpenSignInDialog] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [ChatComponent, setChatComponent] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isShrinked, setIsShrinked] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [location, setLocation] = useState("");
  const [mode, setMode] = useState("");
  const [type, setType] = useState("");
  const [error, setError] = useState(null);

  // Tab state
  const [currentTab, setCurrentTab] = useState(0);

  // Selection state for saved posts
  const [selectedJobs, setSelectedJobs] = useState([]);

  // Pagination state for ListPosts
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0
  });

  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "info",
    action: null,
  });

  // Selection handlers
  const handleToggleSelect = (jobId) => {
    setSelectedJobs((prev) => {
      if (prev.includes(jobId)) {
        return prev.filter((id) => id !== jobId);
      } else {
        return [...prev, jobId];
      }
    });
  };

  const handlePostuler = async () => {
    const candidateId = getUserIdFromAccessToken();
    console.log('Data we are sending: ', candidateId, '---', selectedJobs);

    const endpoint = `${apiUrl}candidates/application/create-matching-profile/`;
    const body = {
      candidate: candidateId,
      post: selectedJobs,
    };

    try {
      const response = await axios.post(endpoint, body);
      console.log('Response v2: ', response);

      if (response.status === 200 || response.status === 201) {
        showSnackbar("Postulation effectuée avec succès", "success");
        setSelectedJobs([]);
      }
    } catch (error) {
      console.error('Error applying:', error);
      showSnackbar("Une erreur est survenue lors de la postulation, vous pouvez postuler qu'une seule fois pour chaque poste", "error");
    }
  };

  const isJobSelected = (jobId) => {
    return selectedJobs.includes(jobId);
  };

  // Show snackbar notification
  const showSnackbar = (message, severity = "success") => {
    setSnackbarState({
      open: true,
      message,
      severity,
    });
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    // Reset search parameters
    setSearchTerm("");
    setLocation("");
    setMode("");
    setType("");
    // Reset pagination
    setPagination({
      currentPage: 1,
      totalPages: 1,
      totalItems: 0
    });
    // Reset selection and fetch new data
    setSelectedJob(null);
    setSelectedJobs([]); // Clear selection when changing tabs
    setAllJobs([]);
    loadData(newValue);
  };

  // Handle search for SearchBar component
  const handleSearch = (event) => {
    event.preventDefault();

    if (searchTerm === "") {
      setPagination({
        ...pagination,
        currentPage: 1,
        totalItems: allJobs.length
      });
      return;
    }

    try {
      const filtered = allJobs.filter((job) => {
        const title = job.post.title.toLowerCase();
        const company = job.post.recruiter?.company?.toLowerCase() || "";
        const searchLower = searchTerm.toLowerCase();
        const locationLower = location.toLowerCase();

        let matchesSearch = true;
        let matchesLocation = true;

        if (searchTerm) {
          matchesSearch = title.includes(searchLower) || company.includes(searchLower);
        }

        if (location) {
          const jobLocation = job.post.localisation?.toLowerCase() || "";
          matchesLocation = jobLocation.includes(locationLower);
        }

        if (mode) {
          if (job.post.mode !== mode) {
            return false;
          }
        }

        if (type) {
          if (job.post.offerType !== type) {
            return false;
          }
        }

        return matchesSearch && matchesLocation;
      });

      setPagination({
        ...pagination,
        currentPage: 1,
        totalItems: filtered.length,
        totalPages: Math.ceil(filtered.length / 10)
      });
    } catch (err) {
      setError("Error filtering jobs");
      console.error("Error filtering jobs:", err);
    }
  };

  // Pagination handlers for ListPosts
  const goToNextPage = () => {
    if (pagination.currentPage < pagination.totalPages) {
      setPagination({
        ...pagination,
        currentPage: pagination.currentPage + 1
      });
    }
  };

  const goToPreviousPage = () => {
    if (pagination.currentPage > 1) {
      setPagination({
        ...pagination,
        currentPage: pagination.currentPage - 1
      });
    }
  };

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.totalPages) {
      setPagination({
        ...pagination,
        currentPage: pageNumber
      });
    }
  };

  // Load data based on the selected tab
  const loadData = async (tabIndex) => {
    setIsLoading(true);
    setError(null);

    try {
      const userId = getUserIdFromAccessToken();
      if (!userId) {
        setIsLoading(false);
        showSnackbar(t('savedPosts.notifications.loginRequired'), "error");
        return;
      }

      // Determine which endpoint to use based on tab
      const endpoint = tabIndex === 0
        ? `${apiUrl}candidates/list_saved_posts/${userId}/`
        : `${apiUrl}candidates/list_saved_spontaneous/${userId}/`;

      console.log(`Fetching data from: ${endpoint}`);
      const response = await fetch(endpoint);

      if (!response.ok) {
        if (response.status === 404) {
          console.error(t(tabIndex === 0 ? 'savedPosts.errors.postSavedNotFound' : 'savedPosts.errors.spontaneousSavedNotFound'));
          setAllJobs([]);
          setSelectedJob(null);
          setIsLoading(false);
          setPagination({
            currentPage: 1,
            totalPages: 0,
            totalItems: 0
          });
          return;
        }
        throw new Error(`${t('savedPosts.errors.apiError')}: ${response.statusText}`);
      }

      const json = await response.json();
      console.log('API response data:', json);

      // Ensure allJobs is always an array
      let jobsArray;

      if (tabIndex === 0) {
        // Regular saved posts
        jobsArray = Array.isArray(json) ? json : [];
      } else {
        // Handle saved_spontaneous nested structure
        if (json && json.saved_spontaneous && Array.isArray(json.saved_spontaneous)) {
          jobsArray = json.saved_spontaneous;
        } else if (Array.isArray(json)) {
          jobsArray = json;
        } else if (json && typeof json === 'object' && json.results) {
          jobsArray = json.results;
        } else {
          jobsArray = [];
        }
      }

      console.log('Processed data array:', jobsArray);
      setAllJobs(jobsArray);
      setPagination({
        currentPage: 1,
        totalPages: Math.ceil(jobsArray.length / 10),
        totalItems: jobsArray.length
      });
    } catch (error) {
      console.error(t('savedPosts.errors.loadError'), error);
      setError(t(tabIndex === 0 ? 'savedPosts.errors.loadSavedPostsFailed' : 'savedPosts.errors.loadSpontaneousFailed'));
      setAllJobs([]);
      setSelectedJob(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Load saved posts from API (keeping original function for compatibility)
  const loadSavedPosts = () => loadData(currentTab);

  // Component initialization
  useEffect(() => {
    loadData(currentTab);
    setIsAuth(!!localStorage.getItem("accessToken"));
  }, []);

  // Add a debug log to check the spontaneous application data structure
  useEffect(() => {
    if (currentTab === 1 && !isLoading) {
      console.log('Spontaneous applications data:', allJobs);
      // Check first item if available to debug structure
      if (allJobs && allJobs.length > 0) {
        console.log('First spontaneous application:', allJobs[0]);
      }
    }
  }, [currentTab, allJobs, isLoading]);

  // Handler for job clicking
  const handleJobClick = (job) => {
    setSelectedJob(job);
    setOpenDialog(true);
  };

  // Dialog handlers
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedJob(null);
  };

  // Login/SignIn handlers
  const handleLoginClick = () => {
    setOpenLoginDialog(true);
  };

  const handleSignInClick = () => {
    setOpenSignInDialog(true);
  };

  const closeLoginPopUp = () => {
    setOpenLoginDialog(false);
  };

  const closeSignInPopUp = () => {
    setOpenSignInDialog(false);
  };

  // Get user ID from token
  function getUserIdFromAccessToken() {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found in local storage");
      return null;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      return decodedToken.user_id;
    } catch (error) {
      console.error("Failed to decode access token", error);
      return null;
    }
  }

  function parseJwt(token) {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  }

  // Get filtered posts based on pagination
  const getPaginatedPosts = () => {
    // Ensure allJobs is an array before filtering
    if (!Array.isArray(allJobs)) {
      console.error('allJobs is not an array:', allJobs);
      return [];
    }

    let filtered = allJobs;

    // Apply filters only for regular posts (tab 0)
    if (currentTab === 0) {
      filtered = allJobs.filter((job) => {
        if (!searchTerm && !location && !mode && !type) return true;

        // Make sure job and job.post exist before accessing properties
        if (!job || !job.post) return false;

        const title = (job.post.title || '').toLowerCase();
        const company = (job.post.recruiter?.company || '').toLowerCase();
        const searchLower = searchTerm.toLowerCase();
        const locationLower = location.toLowerCase();

        let matchesSearch = true;
        let matchesLocation = true;
        let matchesMode = true;
        let matchesType = true;

        if (searchTerm) {
          matchesSearch = title.includes(searchLower) || company.includes(searchLower);
        }

        if (location) {
          const jobLocation = (job.post.localisation || '').toLowerCase();
          matchesLocation = jobLocation.includes(locationLower);
        }

        if (mode) {
          matchesMode = job.post.mode === mode;
        }

        if (type) {
          matchesType = job.post.offerType === type;
        }

        return matchesSearch && matchesLocation && matchesMode && matchesType;
      });
    } else {
      // For spontaneous applications, handle different data structure
      filtered = allJobs.filter((spontaneous) => {
        if (!searchTerm && !location) return true;

        const searchLower = searchTerm.toLowerCase();
        const locationLower = location.toLowerCase();

        let matchesSearch = true;
        let matchesLocation = true;

        if (searchTerm) {
          const position = (spontaneous.position || '').toLowerCase();
          const company = (spontaneous.admin_details?.company || '').toLowerCase();
          matchesSearch = position.includes(searchLower) || company.includes(searchLower);
        }

        if (location) {
          const jobLocation = (spontaneous.localisation || '').toLowerCase();
          matchesLocation = jobLocation.includes(locationLower);
        }

        return matchesSearch && matchesLocation;
      });
    }

    // Calculate start and end indices for pagination
    const startIndex = (pagination.currentPage - 1) * 10;
    const endIndex = Math.min(startIndex + 10, filtered.length);

    return filtered.slice(startIndex, endIndex);
  };

  const renderMainContent = () => {
    if (isLoading) {
      return (
        <>
          <NewAppBar
            isAuth={isAuth}
            onLoginClick={handleLoginClick}
            onSignInClick={handleSignInClick}
            setChatComponent={setChatComponent}
            hideWhileScrolling={90}
          />
          <LoadingContainer>
            <CircularProgress size={60} thickness={4} />
          </LoadingContainer>
        </>
      );
    }

    if (allJobs.length === 0) {
      return (
        <>
          <NewAppBar
            isAuth={isAuth}
            onLoginClick={handleLoginClick}
            onSignInClick={handleSignInClick}
            setChatComponent={setChatComponent}
            hideWhileScrolling={90}
          />
          <Box
            sx={{
              marginLeft: { md: "60px", xs: "0" },
              p: 3
            }}
          >
            <StyledTabs value={currentTab} onChange={handleTabChange} centered>
              <Tab label={t('savedPosts.tabs.savedPosts')} />
              <Tab label={t('savedPosts.tabs.savedSpontaneous')} />
            </StyledTabs>
          </Box>
          <FullScreenContainer>
            <NoSavedPosts
              title={currentTab === 0
                ? t('savedPosts.noSavedItems.posts')
                : t('savedPosts.noSavedItems.spontaneous')
              }
            />
          </FullScreenContainer>
        </>
      );
    }

    const paginatedPosts = getPaginatedPosts();
    console.log('paginatedPosts', paginatedPosts);

    return (
      <Box>
        <NewAppBar
          isAuth={isAuth}
          onLoginClick={handleLoginClick}
          onSignInClick={handleSignInClick}
          setChatComponent={setChatComponent}
          hideWhileScrolling={90}
        />

        <Box
          sx={{
            marginLeft: { md: "60px", xs: "0" },
            p: { xs: 1, sm: 2, md: 3 },
          }}
        >
          {/* <Box
          sx={{
            display: 'flex',
            justifyContent: 'left',
            alignItems: 'center',
            marginBottom: '20px'
          }}
          > */}
          {/* Selection Counter */}
          {selectedJobs.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#E3F2FD",
                borderRadius: "12px",
                padding: "12px 20px",
                marginBottom: "16px",
                border: "1px solid #90CAF9",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    backgroundColor: colors.primary,
                    color: "#fff",
                    borderRadius: "50%",
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 600,
                    fontSize: "14px",
                    fontFamily: "Poppins",
                  }}
                >
                  {selectedJobs.length}
                </Box>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    fontSize: "14px",
                    color: colors.primary,
                  }}
                >
                  {selectedJobs.length === 1
                    ? t("savedPosts.selection.itemSelected", { defaultValue: "offre(s) sélectionné(s)" })
                    : t("savedPosts.selection.itemsSelected", { defaultValue: "offre(s) sélectionné(s)" })}
                </Typography>
              </Box>
              <Button
                onClick={handlePostuler}
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: "13px",
                  textTransform: "none",
                  color: "#FFFFFF",
                  backgroundColor: "#0017E7",
                  borderRadius: "11px",
                  padding: "10px 20px",
                  position: "relative",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  "&:hover": {
                    backgroundColor: "#FFFFFF",
                    color: "#0017E7",
                    border: "1px solid #0017E7",
                    "& .star": {
                      backgroundColor: "#0017E7",
                    },
                    "& img": {
                      content: `url(${smart_logo})`,
                    },
                  },
                  "& .star": {
                    position: "absolute",
                    width: "8px",
                    height: "8px",
                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                    clipPath:
                      "polygon(50% 0%, 75% 38%, 100% 50%, 75% 62%, 50% 100%, 25% 62%, 0% 50%, 25% 38%)",
                    animation: "starShine 3s infinite ease-in-out",
                    "&:nth-of-type(1)": {
                      top: "15%",
                      left: "15%",
                      width: "5px",
                      height: "5px",
                    },
                    "&:nth-of-type(2)": {
                      top: "12%",
                      right: "10%",
                      width: "10px",
                      height: "10px",
                      animationDelay: "0.5s",
                    },
                    "&:nth-of-type(3)": {
                      bottom: "15%",
                      left: "45%",
                      width: "6px",
                      height: "6px",
                      animationDelay: "1s",
                    },
                  },
                  "@keyframes starShine": {
                    "0%, 100%": {
                      opacity: 0.5,
                      transform: "scale(1)",
                    },
                    "50%": {
                      opacity: 1,
                      transform: "scale(1.2)",
                    },
                  },
                  "& img": {
                    position: "relative",
                    zIndex: 3,
                    transition: "all 0.3s ease",
                  },
                }}
              >
                <div className="star" />
                <div className="star" />
                <div className="star" />
                <img
                  src={smart_logo_white}
                  alt="Smart Apply"
                  width={16}
                  height={16}
                />
                {t("savedPosts.multiplApply", { defaultValue: "Postuler à tout" })}
              </Button>
            </Box>
          )}

          <StyledTabs value={currentTab} onChange={handleTabChange} centered>
            <Tab
              sx={{
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: "14px",
                textTransform: "none",
                color: "#D9D9D9",
                "&.Mui-selected": {
                  color: colors.primary,
                },
              }}
              label={t("savedPosts.tabs.savedPosts")}
            />
            <Tab
              sx={{
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: "14px",
                textTransform: "none",
                color: "#D9D9D9",
                "&.Mui-selected": {
                  color: colors.primary,
                },
              }}
              label={t("savedPosts.tabs.savedSpontaneous")} />
          </StyledTabs>
          {/* </Box> */}

          {/* <Box sx={{ textAlign: 'center', my: 3 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: colors.primary,
                fontWeight: 600,
                fontFamily: "Poppins"
              }}
            >
              {currentTab === 0 
                ? t('savedPosts.titles.savedJobPosts')
                : t('savedPosts.titles.savedSpontaneousApplications')
              }
            </Typography>
          </Box> */}

          < SearchBar
            searchTerm={searchTerm}
            location={location}
            showSnackbar={showSnackbar}
            t={t}
            handleSearch={handleSearch}
            setLocation={setLocation}
            setSearchTerm={setSearchTerm}
            setType={setType}
            setMode={setMode}
            placeholder={t("savedPosts.search.placeholder")}
          />

          {currentTab === 0 ? (
            <ListPosts
              setSelectedJob={setSelectedJob}
              setOpenDialog={setOpenDialog}
              posts={paginatedPosts.map((item) => ({
                ...item.post,
                isSaved: true,
                isSpontaneous: false,
              }))}
              loading={isLoading}
              error={error}
              pagination={pagination}
              goToNextPage={goToNextPage}
              goToPreviousPage={goToPreviousPage}
              goToPage={goToPage}
              refreshPosts={loadSavedPosts}
              fromSavedPosts={true}
              spontaneous={false}
              selectedJobs={selectedJobs}
              onToggleSelect={handleToggleSelect}
              isJobSelected={isJobSelected}
            />
          ) : (
            <Container maxWidth="xxl">
              <Box sx={{ mt: { xs: 2, sm: 3, md: 4 } }}>
                <Grid
                  container
                  spacing={{ xs: 2, sm: 2, md: 3, lg: 3 }}
                  sx={{ marginTop: { xs: 2, sm: 3, md: 4 } }}
                >
                  {paginatedPosts.length > 0 ? (
                    paginatedPosts.map((spontaneous) => {
                      // Debug log
                      console.log("Rendering spontaneous item:", spontaneous);

                      // Handle if application_form field exists
                      const spontaneousData =
                        spontaneous.application_form || spontaneous;

                      // Create a complete object with fallbacks for all required fields
                      const completeData = {
                        ...spontaneousData,
                        // Map API fields to expected component fields
                        id: spontaneousData.id,
                        position:
                          spontaneousData.title || "Spontaneous Application", // Backend uses title instead of position
                        description: spontaneousData.description || "",
                        localisation: spontaneousData.localisation || "",
                        updated_at:
                          spontaneousData.updated_at ||
                          spontaneousData.created_at,
                        created_at: spontaneousData.created_at,
                        // Ensure admin_details has all required fields
                        admin_details: {
                          ...spontaneousData.admin_details,
                          company:
                            spontaneousData.admin_details?.company || "Company",
                          comp_industry:
                            spontaneousData.admin_details?.comp_industry ||
                            "NaN",
                          company_logo:
                            spontaneousData.admin_details?.company_logo || null,
                        },
                        // Add post object for useSavePost compatibility
                        post: {
                          id: spontaneousData.id,
                        },
                      };

                      return (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={4}
                          lg={4}
                          xl={3}
                          key={completeData.id || Math.random()}
                        >
                          <SpontaneousCard
                            spontaneous={completeData}
                            setSelectedSpontaneous={setSelectedJob}
                            setOpenDialog={setOpenDialog}
                            refresh={loadSavedPosts}
                          />
                        </Grid>
                      );
                    })
                  ) : (
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="body1">
                          {currentTab === 0
                            ? t("savedPosts.noResults.posts")
                            : t("savedPosts.noResults.spontaneous")}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                {pagination &&
                  pagination.totalPages > 0 &&
                  paginatedPosts.length > 0 && (
                    <div
                      className="pagination-container"
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: "20px",
                        marginBottom: "20px",
                      }}
                    >
                      {isLoading ? (
                        ""
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: 4,
                          }}
                        >
                          <Button
                            onClick={goToPreviousPage}
                            disabled={pagination.currentPage === 1}
                            sx={{ mx: 1 }}
                          >
                            {t("savedPosts.pagination.previous")}
                          </Button>
                          <Typography sx={{ mx: 2, alignSelf: "center" }}>
                            {t("savedPosts.pagination.pageInfo", {
                              current: pagination.currentPage,
                              total: pagination.totalPages,
                            })}
                          </Typography>
                          <Button
                            onClick={goToNextPage}
                            disabled={
                              pagination.currentPage === pagination.totalPages
                            }
                            sx={{ mx: 1 }}
                          >
                            {t("savedPosts.pagination.next")}
                          </Button>
                        </Box>
                      )}
                    </div>
                  )}
              </Box>
            </Container>
          )}
        </Box>
        <Footer />
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth={false}
          PaperProps={{
            style: {
              position: "fixed",
              bottom: 0,
              width: "100%",
              margin: 0,
              borderRadius: 0,
              borderTopLeftRadius: "40px",
              borderTopRightRadius: "40px",
              height: "85vh",
              maxWidth: "none",
              overflowY: "auto",
              left: 0,
              right: 0,
              boxSizing: "border-box",
              // Scrollbar styling
              scrollbarWidth: "thin", // For Firefox
              scrollbarColor: "transparent transparent", // For Firefox
            },
          }}
          BackdropProps={{
            style: {
              backgroundColor: "rgba(0,0,0,0.5)",
            },
          }}
        >
          {selectedJob && (
            <Details
              job={
                currentTab === 0
                  ? {
                    ...selectedJob.post,
                    isSpontaneous: false,
                  }
                  : {
                    ...(selectedJob.application_form || selectedJob),
                    isSpontaneous: true,
                    company: selectedJob.admin_details?.company || "Company",
                    title: selectedJob.title || "Spontaneous Application", // Backend uses title instead of position
                    description: selectedJob.description || "",
                    localisation: selectedJob.localisation || "",
                    mode: selectedJob.mode || "Not specified",
                    created_at:
                      selectedJob.created_at || new Date().toISOString(),
                    updated_at:
                      selectedJob.updated_at || selectedJob.created_at,
                  }
              }
              posts={
                currentTab === 0
                  ? allJobs.map((item) => ({
                    ...item.post,
                    isSpontaneous: false,
                  }))
                  : allJobs.map((item) => {
                    const app = item.application_form || item;
                    return {
                      ...app,
                      isSpontaneous: true,
                      company: app.admin_details?.company || "Company",
                      title: app.title || "Spontaneous Application", // Backend uses title instead of position
                      description: app.description || "",
                      localisation: app.localisation || "",
                      mode: app.mode || "Not specified",
                      created_at: app.created_at || new Date().toISOString(),
                      updated_at: app.updated_at || app.created_at,
                    };
                  })
              }
              onClose={handleCloseDialog}
              spontaneous={currentTab === 1}
            />
          )}
        </Dialog>
      </Box>
    );
  };

  return (
    <>
      {renderMainContent()}

      <Dialog open={openLoginDialog} onClose={closeLoginPopUp} maxWidth="sm">
        <DialogContent>
          <LoginForDialog />
        </DialogContent>
        <DialogActions />
      </Dialog>

      <Dialog open={openSignInDialog} onClose={closeSignInPopUp} maxWidth="sm">
        <DialogContent>
          <SignInForDialog />
        </DialogContent>
        <DialogActions />
      </Dialog>

      <Snackbar
        open={snackbarState.open}
        autoHideDuration={6000}
        onClose={() => setSnackbarState((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            width: 'auto',
            maxWidth: { xs: '90vw', sm: '70vw', md: '50vw', lg: '40vw' },
          }
        }}
      >
        <Alert
          severity={snackbarState.severity}
          onClose={() => setSnackbarState((prev) => ({ ...prev, open: false }))}
          sx={{
            width: 'auto',
            maxWidth: { xs: '90vw', sm: '70vw', md: '50vw', lg: '40vw' },
            fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' },
            '& .MuiAlert-message': {
              wordBreak: 'break-word',
            }
          }}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </>
  );
}

const mapStateToProps = (state) => ({
  userId: state.userId,
});

const mapDispatchToProps = {
  setUserId,
};

export default connect(mapStateToProps, mapDispatchToProps)(SavedPosts);
